import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, FileText } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';

// PR-D5 — build an invoice as a flexible basket: pick a client, tick the
// finished trips to include. Unticked trips stay available for next time. The
// system only computes the total; the operator creates and (later) sends it.

interface ClientRow { client_id: string; name_en: string }
interface TripRow {
  id: string; code: string; trip_date: string; ticket_number: string | null;
  net_weight_tons: string | null; rate_per_ton_sar: string | null; revenue_sar: string | null; vehicle_plate: string | null;
}

const inputCls = 'h-9 rounded-md border border-input bg-background px-3 text-sm';
const num = (v: string | null) => (v != null ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—');

export default function DumperInvoiceBuilderPage() {
  const navigate = useNavigate();
  const [clientId, setClientId] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const { data: clients } = useQuery({
    queryKey: ['dumper-contracts'],
    queryFn: async () => (await apiClient.get<{ items: ClientRow[] }>('/api/v1/dumper-contracts')).data.items,
  });
  const { data: trips, isLoading } = useQuery({
    queryKey: ['dumper-uninvoiced', clientId],
    enabled: !!clientId,
    queryFn: async () => (await apiClient.get<{ items: TripRow[] }>('/api/v1/dumper-invoices/uninvoiced', { params: { clientId } })).data.items,
  });

  const rows = trips ?? [];
  const totals = useMemo(() => {
    let tons = 0, sar = 0;
    for (const t of rows) if (picked.has(t.id)) { tons += Number(t.net_weight_tons ?? 0); sar += Number(t.revenue_sar ?? 0); }
    return { tons, sar };
  }, [rows, picked]);

  function toggle(id: string) {
    setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function setClient(id: string) { setClientId(id); setPicked(new Set()); }

  const createMut = useMutation({
    mutationFn: async () => apiClient.post<{ id: string; code: string }>('/api/v1/dumper-invoices', { clientId, tripIds: [...picked] }),
    onSuccess: () => navigate('/dashboard/dumpers/receivables'),
  });

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">Dumpers · Build an invoice</div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/dumpers/receivables')} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Receivables
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-8">
        <div className="mb-4">
          <label className="text-xs text-muted-foreground">Client</label>
          <select className={`${inputCls} w-full sm:w-80 block`} value={clientId} onChange={(e) => setClient(e.target.value)}>
            <option value="">Select client…</option>
            {(clients ?? []).map((c) => <option key={c.client_id} value={c.client_id}>{c.name_en}</option>)}
          </select>
        </div>

        {clientId && (
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2"></th>
                  <th className="text-start font-medium px-3 py-2">Code</th>
                  <th className="text-start font-medium px-3 py-2">Date</th>
                  <th className="text-start font-medium px-3 py-2">Ticket</th>
                  <th className="text-start font-medium px-3 py-2">Truck</th>
                  <th className="text-end font-medium px-3 py-2">Tons</th>
                  <th className="text-end font-medium px-3 py-2">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
                {!isLoading && rows.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">No finished, uninvoiced loads for this client.</td></tr>
                )}
                {rows.map((t) => (
                  <tr key={t.id} className={`border-t border-border ${picked.has(t.id) ? 'bg-primary/5' : ''}`}>
                    <td className="px-3 py-2"><input type="checkbox" checked={picked.has(t.id)} onChange={() => toggle(t.id)} /></td>
                    <td className="px-3 py-2 font-mono text-xs">{t.code}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{t.trip_date?.slice(0, 10)}</td>
                    <td className="px-3 py-2">{t.ticket_number ?? '—'}</td>
                    <td className="px-3 py-2">{t.vehicle_plate ?? '—'}</td>
                    <td className="px-3 py-2 text-end">{num(t.net_weight_tons)}</td>
                    <td className="px-3 py-2 text-end">{num(t.revenue_sar)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {clientId && rows.length > 0 && (
          <div className="mt-5 flex items-center justify-between rounded-xl border border-border bg-card p-4">
            <div className="text-sm">
              <b>{picked.size}</b> loads · <b>{totals.tons.toLocaleString(undefined, { maximumFractionDigits: 2 })}</b> tons ·
              {' '}<b>{totals.sar.toLocaleString(undefined, { maximumFractionDigits: 2 })} SAR</b>
              <div className="text-xs text-muted-foreground">Creates a draft invoice. Nothing is sent.</div>
            </div>
            <Button disabled={picked.size === 0 || createMut.isPending} onClick={() => createMut.mutate()} className="gap-2">
              <FileText className="h-4 w-4" /> {createMut.isPending ? 'Creating…' : 'Create invoice'}
            </Button>
          </div>
        )}
        {createMut.isError && <div className="text-sm text-rose-500 mt-3">Could not create the invoice.</div>}
      </main>
    </div>
  );
}
